import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const JamWebSocketContext = createContext(null);

export const useJamWebSocket = () => {
  const context = useContext(JamWebSocketContext);
  if (!context) {
    throw new Error('useJamWebSocket must be used within JamWebSocketProvider');
  }
  return context;
};

export const JamWebSocketProvider = ({ children }) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const reconnectTimeout = useRef(null);
  const listeners = useRef([]);

  const connect = (sessionId) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.close();
    }

    // ✅ FIX: Add /api prefix for Kubernetes ingress routing
    const wsUrl = `${WS_URL}/api/ws/jam/${sessionId}`;
    console.log('🔌 Connecting to jam session WebSocket:', wsUrl);
    console.log('   Backend URL:', BACKEND_URL);
    console.log('   WS URL Base:', WS_URL);
    console.log('   Session ID:', sessionId);

    try {
      ws.current = new WebSocket(wsUrl);
    } catch (error) {
      console.error('❌ Failed to create WebSocket:', error);
      setIsConnected(false);
      return;
    }

    ws.current.onopen = () => {
      console.log('✅ WebSocket connected to session:', sessionId);
      setIsConnected(true);
      setCurrentSessionId(sessionId);
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', data);
        setLastMessage(data);
        
        // Notify all listeners
        listeners.current.forEach(listener => listener(data));
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.current.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      console.error('   WebSocket URL:', wsUrl);
      console.error('   ReadyState:', ws.current?.readyState);
      console.error('   Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
    };

    ws.current.onclose = (event) => {
      console.log('🔌 WebSocket disconnected');
      console.log('   Close code:', event.code);
      console.log('   Close reason:', event.reason);
      console.log('   Was clean:', event.wasClean);
      setIsConnected(false);
      setCurrentSessionId(null);
      
      // Don't auto-reconnect for now to avoid connection loops
      // Attempt to reconnect after 3 seconds
      // reconnectTimeout.current = setTimeout(() => {
      //   if (sessionId) {
      //     console.log('🔄 Attempting to reconnect...');
      //     connect(sessionId);
      //   }
      // }, 3000);
    };
  };

  const disconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    setIsConnected(false);
    setCurrentSessionId(null);
  };

  const send = (message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      console.log('📤 WebSocket message sent:', message);
    } else {
      console.error('WebSocket is not connected');
    }
  };

  const addListener = (callback) => {
    listeners.current.push(callback);
    return () => {
      listeners.current = listeners.current.filter(l => l !== callback);
    };
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const value = {
    connect,
    disconnect,
    send,
    addListener,
    isConnected,
    currentSessionId,
    lastMessage
  };

  return (
    <JamWebSocketContext.Provider value={value}>
      {children}
    </JamWebSocketContext.Provider>
  );
};
